/* eslint react/destructuring-assignment: off */
import PropTypes from 'prop-types';
import React from 'react';
import Link from 'components/ui/Link';
import KeyedListMapper from 'components/core/KeyedListMapper';
import TranslatedText from 'components/i18n/TranslatedText';
import styled, { css } from 'styled-components';
import { BACKGROUND_ACTIVE, TITLE_FONT } from 'style/mixins';
import {
  DisclosureTriangle,
  Image,
  linkImage,
  renderHeaderChildLinks,
} from 'components/header/styles';
import { ITEM } from 'injection-classes';
import { ACCENT_PRIMARY, SURFACE_4, TEXT_100, TEXT_CONTRAST_ON_ACCENT_PRIMARY } from 'style/constants';

// TODO: fix this!!! https://maestrojira.atlassian.net/browse/NS-1401
const FORTNITE_DOMAIN = 'watch.fortnite.com';

const ChildHeaderLink = renderHeaderChildLinks(Link);

const LinkBody = styled.div`
  color: ${props => props.currentlyOn ?
    ACCENT_PRIMARY(props) :
    TEXT_100(props)};
  position: absolute;
  min-width: 100%;
  z-index: 10;
`;
const ImageLink = linkImage(Link);

const HeaderLink = styled(Link).attrs({ plain: true })`
  ${TITLE_FONT};
  background: ${props => props.currentlyOn || props.expanded ?
    ACCENT_PRIMARY(props) :
    'transparent'};
  padding: 5px;
  display: flex;
  flex: 0 0 auto;

  &:hover {
    ${BACKGROUND_ACTIVE}
    cursor: ${props => (props.href ? 'pointer' : 'default')};
    & > * {
      color: ${TEXT_CONTRAST_ON_ACCENT_PRIMARY};
    }
  }
  & > * {
    color: ${props => props.currentlyOn || props.expanded ?
    TEXT_CONTRAST_ON_ACCENT_PRIMARY(props) :
    TEXT_100(props)};
  }

  ${props => props.expanded && css`
    & ${DisclosureTriangle} {
      border-color: transparent transparent ${TEXT_CONTRAST_ON_ACCENT_PRIMARY(props)} transparent;
    }
  `};
`;

const ChildHeaderLinkWrapper = styled.div`
  background: ${SURFACE_4};
  ${TITLE_FONT};
  display: flex;
`;

const IECompatibleMinContentWrapper = styled.div`
  display: -ms-grid;
  -ms-grid-columns: min-content;
`;

const LinkGroup = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: min-content;
  white-space: nowrap;
  flex-shrink: 0;
  height: 100%;
  width: auto;
`;

export default class HeaderItem extends React.Component {
  static propTypes = {
    currentRoute: PropTypes.string.isRequired,
    domRef: PropTypes.func,
    fullWidth: PropTypes.bool,
    hasMenu: PropTypes.oneOf(['yes', 'no']), // This is the literal worst
    image: PropTypes.string,
    index: PropTypes.number.isRequired,
    link: PropTypes.string,
    menuItems: PropTypes.arrayOf(
      PropTypes.shape({
        link: PropTypes.string,
        text: PropTypes.string,
      }),
    ),
    onTrackNav: PropTypes.func.isRequired,
    overflow: PropTypes.bool,
    siteDomain: PropTypes.string.isRequired,
    text: PropTypes.string,
    visible: PropTypes.bool,
  };

  static defaultProps = {
    domRef: () => { },
    fullWidth: false,
    hasMenu: 'no',
    image: null,
    link: null,
    menuItems: null,
    overflow: false,
    text: '',
    visible: true,
  };

  state = {
    expanded: false,
  };

  componentDidUpdate() {
    if (this.state.expanded && !this.enableMenu) {
      /* eslint-disable-next-line react/no-did-update-set-state */
      this.setState({
        expanded: false,
      });
    }
  }

  get enableMenu() {
    return (
      this.props.hasMenu === 'yes' &&
      Array.isArray(this.props.menuItems) &&
      this.props.menuItems.length
    );
  }

  handleMouseEnter = () => {
    if (!this.enableMenu) {
      return;
    }
    this.setState({ expanded: true });
  };

  handleMouseLeave = () => {
    if (!this.enableMenu) {
      return;
    }
    this.setState({ expanded: false });
  };

  handleToggle = () => {
    if (this.enableMenu) {
      this.setState(({ expanded }) => ({ expanded: !expanded }));
    }
  };

  render() {
    const {
      currentRoute,
      domRef,
      fullWidth,
      image,
      link,
      menuItems,
      onTrackNav,
      overflow,
      index,
      siteDomain,
      text,
      visible,
    } = this.props;
    const { expanded } = this.state;
    const hasChildren = this.enableMenu;

    const parentStyle = { visibility: visible ? undefined : 'hidden' };
    if (fullWidth) {
      parentStyle.width = '100%';
    }

    const menuStyle = overflow ?
      { right: '100%', top: 0 } :
      { left: 0, top: '100%' };

    return (
      <IECompatibleMinContentWrapper ref={domRef} style={parentStyle}>
        <LinkGroup
          className={ITEM}
          onClick={this.handleToggle}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
          style={fullWidth ? { width: '100%' } : null}
        >
          {image ? (
            <ImageLink
              href={link}
              onClick={() => onTrackNav('link', text, link, image)}
            >
              <Image src={image} />
            </ImageLink>
          ) : (
            <HeaderLink
              currentlyOn={link === currentRoute}
              expanded={expanded}
              href={this.enableMenu ? undefined : link}
              onClick={() => onTrackNav(hasChildren ? 'menu' : 'link', text, link)}
            >
              {/** TODO: fix this!! https://maestrojira.atlassian.net/browse/NS-1401 */}
              {siteDomain === FORTNITE_DOMAIN &&
              !Number.isNaN(Number(index)) ? (
                <TranslatedText
                  fallback={text}
                  stringKey={`HEADER_MENU_${index + 1}_TEXT`}
                />
                ) : (
                  <span>{text}</span>
                )}
              {hasChildren && <DisclosureTriangle expanded={expanded} />}
            </HeaderLink>
          )}
          {hasChildren && expanded && (
            <LinkBody style={menuStyle}>
              <KeyedListMapper keyField="text" list={menuItems}>
                {(_key, { link: childLink, text: childText }) => (
                  <ChildHeaderLinkWrapper
                    key={_key}
                    isParentImage={Boolean(image)}
                  >
                    <ChildHeaderLink
                      currentlyOn={childLink === currentRoute}
                      href={childLink}
                      onClick={() => onTrackNav('link', childText, childLink)}
                    >
                      <span>{childText}</span>
                    </ChildHeaderLink>
                  </ChildHeaderLinkWrapper>
                )}
              </KeyedListMapper>
            </LinkBody>
          )}
        </LinkGroup>
      </IECompatibleMinContentWrapper>
    );
  }
}
